import React, { useEffect, useRef, useState } from "react";
import "./SolitaireGame.css";
import { useFirebase } from "../contexts/FirebaseContext";
import { useLanguage } from "../contexts/LanguageContext";
import SolitaireHighScores from "./SolitaireHighScores";
import {
  SUIT_NAME,
  SUIT_SYMBOL,
  createGame,
  drawCard,
  isRed,
  isWon,
  listHints,
  moveCards,
  rankLabel,
  sameSelection,
} from "../utils/solitaire.mjs";

const CARD_BACK = `${process.env.PUBLIC_URL}/solitaire/card-back.jpg`;

const formatTime = (seconds) => {
  const total = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const cardName = (card) => `${rankLabel(card.rank)} of ${SUIT_NAME[card.suit]}`;

// x, y are percentages. A trailing 1 means the pip is turned upside down.
const PIP_LAYOUT = {
  1: [[50, 50]],
  2: [[50, 20], [50, 80, 1]],
  3: [[50, 18], [50, 50], [50, 82, 1]],
  4: [[28, 22], [72, 22], [28, 78, 1], [72, 78, 1]],
  5: [[28, 20], [72, 20], [50, 50], [28, 80, 1], [72, 80, 1]],
  6: [[28, 18], [72, 18], [28, 50], [72, 50], [28, 82, 1], [72, 82, 1]],
  7: [[28, 16], [72, 16], [50, 33], [28, 50], [72, 50], [28, 84, 1], [72, 84, 1]],
  8: [[28, 14], [72, 14], [28, 38], [72, 38], [28, 62, 1], [72, 62, 1], [28, 86, 1], [72, 86, 1]],
  9: [[28, 13], [72, 13], [28, 33], [72, 33], [50, 50], [28, 67, 1], [72, 67, 1], [28, 87, 1], [72, 87, 1]],
  10: [[28, 12], [72, 12], [50, 26], [28, 37], [72, 37], [28, 63, 1], [72, 63, 1], [50, 74, 1], [28, 88, 1], [72, 88, 1]],
};

const SuitShape = ({ suit }) => {
  if (suit === "d") {
    return (
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <path d="M50 4 L94 50 L50 96 L6 50 Z" fill="currentColor" />
      </svg>
    );
  }
  if (suit === "h") {
    return (
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <path
          d="M50 90 C18 66 4 50 4 32 C4 16 16 6 30 6 C40 6 47 12 50 22 C53 12 60 6 70 6 C84 6 96 16 96 32 C96 50 82 66 50 90 Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  if (suit === "c") {
    return (
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="30" r="16" fill="currentColor" />
        <circle cx="30" cy="52" r="16" fill="currentColor" />
        <circle cx="70" cy="52" r="16" fill="currentColor" />
        <path d="M44 58 L50 94 L56 58 Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <path
        d="M50 6 C80 34 96 48 96 64 C96 80 84 90 70 90 C60 90 54 84 50 76 C46 84 40 90 30 90 C16 90 4 80 4 64 C4 48 20 34 50 6 Z"
        fill="currentColor"
      />
      <path d="M44 72 L50 96 L56 72 Z" fill="currentColor" />
    </svg>
  );
};

const CardIndex = ({ label, suit, flip, ten }) => (
  <span className={`solitaire-index ${flip ? "is-flip" : ""} ${ten ? "is-ten" : ""}`}>
    <span className="solitaire-index-rank">{label}</span>
    <span className="solitaire-index-suit" aria-hidden="true">
      <SuitShape suit={suit} />
    </span>
  </span>
);

const CardFace = ({ card }) => {
  const label = rankLabel(card.rank);
  const pips = PIP_LAYOUT[card.rank];
  const ten = card.rank === 10;
  return (
    <span className="solitaire-face">
      <CardIndex label={label} suit={card.suit} ten={ten} />
      <CardIndex label={label} suit={card.suit} ten={ten} flip />
      {pips ? (
        <span className={`solitaire-pips ${card.rank === 1 ? "is-ace" : ""}`} aria-hidden="true">
          {pips.map(([x, y, flip]) => (
            <span
              key={`${x}-${y}`}
              className="solitaire-dot"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: `translate(-50%, -50%)${flip ? " rotate(180deg)" : ""}`,
              }}
            >
              <SuitShape suit={card.suit} />
            </span>
          ))}
        </span>
      ) : (
        <span className="solitaire-court" aria-hidden="true">
          <span className="solitaire-court-suit">
            <SuitShape suit={card.suit} />
          </span>
          <span className="solitaire-court-letter">{label}</span>
          <span className="solitaire-court-suit is-flip">
            <SuitShape suit={card.suit} />
          </span>
        </span>
      )}
    </span>
  );
};

const SolitaireCard = ({ card, selected, hinted, style, onClick, label }) => (
  <button
    type="button"
    className={`solitaire-card ${card.faceUp ? "is-up" : "is-down"} ${
      selected ? "is-selected" : ""
    } ${hinted ? "is-hint" : ""} ${card.faceUp && isRed(card.suit) ? "is-red" : "is-black"}`}
    style={style}
    onClick={onClick}
    aria-label={label || (card.faceUp ? cardName(card) : "Face-down card")}
  >
    {card.faceUp ? (
      <CardFace card={card} />
    ) : (
      <span
        className="solitaire-back"
        style={{ backgroundImage: `url(${CARD_BACK})` }}
        aria-hidden="true"
      />
    )}
  </button>
);

const SolitaireGame = () => {
  const { t } = useLanguage();
  const { saveSolitaireScore, points, addPoints } = useFirebase();
  const [board, setBoard] = useState(() => createGame());
  const [selection, setSelection] = useState(null);
  const [moves, setMoves] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [won, setWon] = useState(false);
  const [result, setResult] = useState(null);
  const [tick, setTick] = useState(0);
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 760 : false
  );
  const [hintsUsed, setHintsUsed] = useState(0);
  const [hintCursor, setHintCursor] = useState(0);
  const [hint, setHint] = useState(null);
  const [hintNote, setHintNote] = useState("");
  const movesRef = useRef(0);
  const startedRef = useRef(null);
  const savedRef = useRef(false);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!startedAt || won) return undefined;
    const id = window.setInterval(() => setTick((value) => value + 1), 250);
    return () => window.clearInterval(id);
  }, [startedAt, won]);

  useEffect(() => {
    if (!result || savedRef.current) return;
    savedRef.current = true;
    saveSolitaireScore(result);
  }, [result, saveSolitaireScore]);

  const elapsed = result
    ? result.timeSec
    : startedAt
      ? (Date.now() - startedAt) / 1000
      : 0;
  void tick;

  const overlapUp = narrow ? 16 : 26;
  const overlapDown = narrow ? 10 : 16;

  const commit = (next) => {
    const start = startedRef.current ?? Date.now();
    startedRef.current = start;
    const count = movesRef.current + 1;
    movesRef.current = count;
    setStartedAt(start);
    setMoves(count);
    setBoard(next);
    setSelection(null);
    setHint(null);
    setHintNote("");
    if (isWon(next)) {
      setWon(true);
      setResult({
        timeSec: Math.round(((Date.now() - start) / 1000) * 10) / 10,
        moves: count,
      });
    }
  };

  const deal = () => {
    movesRef.current = 0;
    startedRef.current = null;
    savedRef.current = false;
    setBoard(createGame());
    setSelection(null);
    setMoves(0);
    setStartedAt(null);
    setWon(false);
    setResult(null);
    setHintsUsed(0);
    setHintCursor(0);
    setHint(null);
    setHintNote("");
  };

  const hintCost = 2 + hintsUsed;

  const useHint = () => {
    const options = listHints(board);
    if (!options.length) {
      setHint(null);
      setHintNote(t("solitaireHintNone"));
      return;
    }
    if (points < hintCost) {
      setHintNote(t("solitaireHintNeed").replace("{cost}", String(hintCost)));
      return;
    }
    const next = options[hintCursor % options.length];
    addPoints(-hintCost);
    setHintsUsed((count) => count + 1);
    setHintCursor((cursor) => cursor + 1);
    setHint(next);
    if (next.draw) {
      setHintNote(t("solitaireHintDraw"));
      return;
    }
    const card =
      next.from.zone === "waste"
        ? board.waste[board.waste.length - 1]
        : board.tableau[next.from.col][next.from.index];
    setHintNote(
      t("solitaireHintMove").replace(
        "{card}",
        `${rankLabel(card.rank)}${SUIT_SYMBOL[card.suit]}`
      )
    );
  };

  const hintSource = (zone, col, index) => {
    if (!hint || hint.draw || hint.from.zone !== zone) return false;
    if (zone === "waste") return true;
    return hint.from.col === col && index >= hint.from.index;
  };

  const hintDest = (zone, col) => {
    if (!hint || hint.draw || !hint.to || hint.to.zone !== zone) return false;
    if (zone === "foundation") return hint.to.pile === col;
    return hint.to.col === col;
  };

  const tryDrop = (from, to) => {
    if (!selection || sameSelection(selection, from)) return false;
    const next = moveCards(board, selection, to);
    if (!next) return false;
    commit(next);
    return true;
  };

  const selectOrMove = (from, to) => {
    if (tryDrop(from, to)) return;
    setSelection((current) => (sameSelection(current, from) ? null : from));
  };

  const onStock = () => {
    const next = drawCard(board);
    if (!next) return;
    commit(next);
  };

  const onWaste = (event) => {
    const from = { zone: "waste" };
    if (event.detail >= 2) {
      const next = moveCards(board, from, { zone: "foundation" });
      if (next) {
        commit(next);
        return;
      }
    }
    setSelection((current) => (sameSelection(current, from) ? null : from));
  };

  const onFoundation = (pile, event) => {
    const from = { zone: "foundation", pile };
    const hasCard = board.foundations[pile].length > 0;
    if (hasCard && event.detail >= 2) {
      return;
    }
    if (selection) {
      const next = moveCards(board, selection, { zone: "foundation", pile });
      if (next) {
        commit(next);
        return;
      }
    }
    if (!hasCard) return;
    setSelection((current) => (sameSelection(current, from) ? null : from));
  };

  const onTableau = (col, index, event) => {
    const card = board.tableau[col][index];
    const from = { zone: "tableau", col, index };
    const dropTo = { zone: "tableau", col };
    if (card.faceUp && event.detail >= 2) {
      const next = moveCards(board, from, { zone: "foundation" });
      if (next) {
        commit(next);
        return;
      }
    }
    if (!card.faceUp) {
      tryDrop(from, dropTo);
      return;
    }
    selectOrMove(from, dropTo);
  };

  const onEmptyColumn = (col) => {
    if (!selection) return;
    const next = moveCards(board, selection, { zone: "tableau", col });
    if (next) commit(next);
  };

  const pileOffset = (pile, index) => {
    let top = 0;
    for (let i = 0; i < index; i += 1) {
      top += pile[i].faceUp ? overlapUp : overlapDown;
    }
    return top;
  };

  return (
    <div className="solitaire-page">
      <div className="solitaire-panel">
        <header className="solitaire-header">
          <h1>{t("solitaire")}</h1>
          <div className="solitaire-stats">
            <span>
              {t("time")} {formatTime(elapsed)}
            </span>
            <span>
              {t("moves")} {result ? result.moves : moves}
            </span>
          </div>
          {!won && (
            <div className="solitaire-actions">
              <button type="button" className="solitaire-hint" onClick={useHint}>
                {t("solitaireHint")} · {hintCost}
              </button>
              <button type="button" className="solitaire-deal" onClick={deal}>
                {t("solitaireNewDeal")}
              </button>
            </div>
          )}
        </header>
        {hintNote && !won && <p className="solitaire-hint-note">{hintNote}</p>}

        {won ? (
          <div className="solitaire-won">
            <p className="solitaire-won-title">{t("solitaireWon")}</p>
            <p className="solitaire-won-detail">
              {formatTime(result.timeSec)} · {t("moves")} {result.moves}
            </p>
            <SolitaireHighScores
              currentTimeSec={result.timeSec}
              currentMoves={result.moves}
            />
            <button type="button" className="solitaire-deal" onClick={deal}>
              {t("playAgain")}
            </button>
          </div>
        ) : (
          <div className="solitaire-table">
            <div className="solitaire-top">
              <button
                type="button"
                className={`solitaire-slot solitaire-stock ${
                  board.stock.length ? "has-cards" : ""
                } ${hint?.draw ? "is-hint" : ""}`}
                onClick={onStock}
                aria-label={t("solitaireStock")}
              >
                {board.stock.length > 0 && (
                  <span
                    className="solitaire-back"
                    style={{ backgroundImage: `url(${CARD_BACK})` }}
                  />
                )}
              </button>
              <div className="solitaire-slot">
                {board.waste.length > 0 && (
                  <SolitaireCard
                    card={board.waste[board.waste.length - 1]}
                    selected={selection?.zone === "waste"}
                    hinted={hintSource("waste")}
                    onClick={onWaste}
                  />
                )}
              </div>
              <div className="solitaire-foundations">
                {board.foundations.map((pile, pileIndex) => {
                  const top = pile[pile.length - 1];
                  return (
                    <div
                      key={pileIndex}
                      className={`solitaire-slot ${
                        hintDest("foundation", pileIndex) ? "is-hint" : ""
                      }`}
                    >
                      {top ? (
                        <SolitaireCard
                          card={top}
                          selected={
                            selection?.zone === "foundation" &&
                            selection.pile === pileIndex
                          }
                          onClick={(event) => onFoundation(pileIndex, event)}
                        />
                      ) : (
                        <button
                          type="button"
                          className="solitaire-slot-hit"
                          aria-label={t("solitaireFoundation")}
                          onClick={(event) => onFoundation(pileIndex, event)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="solitaire-tableau">
              {board.tableau.map((pile, col) => {
                const height =
                  pile.length === 0
                    ? 0
                    : pileOffset(pile, pile.length - 1);
                return (
                  <div
                    key={col}
                    className={`solitaire-column ${pile.length === 0 ? "is-empty" : ""}`}
                  >
                    {pile.length === 0 ? (
                      <button
                        type="button"
                        className={`solitaire-slot solitaire-empty ${
                          hintDest("tableau", col) ? "is-hint" : ""
                        }`}
                        aria-label={t("solitaireEmpty")}
                        onClick={() => onEmptyColumn(col)}
                      />
                    ) : (
                      pile.map((card, index) => (
                        <SolitaireCard
                          key={card.id}
                          card={card}
                          selected={
                            selection?.zone === "tableau" &&
                            selection.col === col &&
                            index >= selection.index &&
                            card.faceUp
                          }
                          hinted={
                            hintSource("tableau", col, index) ||
                            (hintDest("tableau", col) && index === pile.length - 1)
                          }
                          style={{ top: pileOffset(pile, index), zIndex: index + 1 }}
                          onClick={(event) => onTableau(col, index, event)}
                        />
                      ))
                    )}
                    {pile.length > 0 && <span className="solitaire-column-spacer" style={{ height }} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SolitaireGame;
